import { useEffect, useRef, useState } from 'react';

import { Button } from '@/shared/ui/button';
import { ProgressBar } from '@/shared/ui/progress-bar';

import { FieldChatMarkdown } from './field-chat-markdown';
import { FindingResponse } from './field-discovery';
import { FieldStatus, FieldTime } from './field-primitives';
import { FieldTicketForm } from './field-ticket-form';

import type { FieldPanelProps } from './field-types';
import type { FieldDestination } from '../field-overview';
import type { FieldPr, FieldReviewEvidence, FieldReviewStage } from '@mio/shared';

const stageLabels: Record<FieldReviewStage, string> = {
  queued: 'Waiting to start',
  preparing: 'Preparing your project context',
  checking: 'Checking the submitted version',
  reviewing: 'Reviewing the implementation',
  synthesizing: 'Writing the review report',
  completed: 'Review complete',
  failed: 'Review interrupted',
};

function Evidence({
  evidence,
  snapshot,
  onNavigate,
}: {
  evidence: FieldReviewEvidence[];
  snapshot: FieldPanelProps['snapshot'];
  onNavigate?: ((destination: FieldDestination) => void) | undefined;
}) {
  if (!evidence.length) return null;
  return (
    <details className="field-review-evidence">
      <summary>Evidence ({evidence.length})</summary>
      <ul>
        {evidence.map((item, index) => {
          const referencedRun = snapshot.runs.find(
            (run) =>
              run.id === item.referenceId ||
              (item.kind === 'browser' &&
                run.browserObservation?.captures?.some(
                  (capture) => `${run.id}:${capture.id}` === item.referenceId,
                )),
          );
          const captures =
            item.kind === 'browser' &&
            referencedRun?.trusted &&
            referencedRun.browserObservation?.identityVerified
              ? referencedRun.browserObservation.captures?.filter(
                  (capture) =>
                    item.referenceId === referencedRun.id ||
                    item.referenceId === `${referencedRun.id}:${capture.id}`,
                )
              : undefined;
          const destination: FieldDestination | undefined = referencedRun
            ? { section: 'operations', focusId: referencedRun.id }
            : snapshot.discovery?.decisions.some((decision) => decision.id === item.referenceId)
              ? { section: 'engagement', focusId: item.referenceId }
              : snapshot.documents.some((document) => document.id === item.referenceId)
                ? { section: 'documents', focusId: item.referenceId }
                : undefined;
          return (
            <li key={`${item.referenceId}-${index}`}>
              <span className="field-object-meta">
                {item.kind === 'source'
                  ? 'Source inspection'
                  : item.kind === 'test'
                    ? 'Test observation'
                    : item.kind === 'browser'
                      ? 'Browser observation'
                      : 'Requirement'}
              </span>
              {item.path ? (
                <code className="block break-all">
                  {item.path}
                  {item.startLine
                    ? `:${item.startLine}${item.endLine && item.endLine !== item.startLine ? `–${item.endLine}` : ''}`
                    : ''}
                </code>
              ) : null}
              <p>{item.explanation}</p>
              {captures?.map((capture) => (
                <figure className="field-review-capture" key={capture.id}>
                  <img
                    src={`data:${capture.mimeType};base64,${capture.imageBase64}`}
                    alt={`Submitted application: ${capture.id} browser checkpoint`}
                    width={capture.width}
                    height={capture.height}
                    loading="lazy"
                  />
                  <figcaption>
                    {capture.id} checkpoint · captured <FieldTime value={capture.capturedAt} />
                    <details>
                      <summary>Capture attribution and observed text</summary>
                      <p>Trusted run: {referencedRun?.id}</p>
                      <p className="break-all">Image SHA-256: {capture.imageSha256}</p>
                      <p className="whitespace-pre-wrap">{capture.visibleText}</p>
                      {capture.textTruncated ? <p>Observed text was truncated.</p> : null}
                    </details>
                  </figcaption>
                </figure>
              ))}
              {destination && onNavigate ? (
                <button className="text-link" type="button" onClick={() => onNavigate(destination)}>
                  Open referenced evidence
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>
    </details>
  );
}

export function FieldReviewReportCard({
  snapshot,
  busy,
  onAction,
  pr,
  review,
  onNavigate,
  focusId,
  focusRequest,
}: FieldPanelProps & {
  pr: FieldPr;
  review: FieldPr['reviews'][number];
  onNavigate?: ((destination: FieldDestination) => void) | undefined;
  focusId?: string | undefined;
  focusRequest?: number;
}) {
  const [ticketFinding, setTicketFinding] = useState<string | null>(null);
  const [responding, setResponding] = useState<string | null>(null);
  const actor = snapshot.actors.find((item) => item.id === review.actorId);
  const report = review.report;
  const stale = review.revision !== pr.revision;
  const superseded =
    pr.reviews
      .filter((item) => item.actorId === review.actorId && item.revision === review.revision)
      .at(-1)?.id !== review.id;
  const historical = stale || superseded;
  const operation = snapshot.operations.find((item) => item.id === review.operationId);
  const failed = review.status === 'failed' || operation?.status === 'failed';
  const cancelled = failed && operation?.error === 'operation_cancelled';
  const pending = !failed && review.status === 'pending';
  const queued = !review.stage || review.stage === 'queued';
  const savedProgress = queued && (review.progress?.completed ?? 0) > 0;
  const focused = focusId === `review-${review.id}`;
  const history = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    if (focused && history.current) history.current.open = true;
  }, [focused, focusRequest]);
  const summary = report?.summary ?? review.summary;
  const summaryRepeatsProgress =
    pending && !queued && !report && summary === review.progress?.detail;
  const findingForTicket = snapshot.discovery?.findings.find((item) => item.id === ticketFinding);
  const body = (
    <div className="field-review-report-body">
      {pending ? (
        <div className="field-review-progress" role="status">
          <p>{stageLabels[review.stage ?? 'queued']}</p>
          {!queued && review.progress?.detail ? (
            <p className="text-fg-muted text-sm">{review.progress.detail}</p>
          ) : null}
          {savedProgress ? (
            <p className="text-fg-muted text-sm">Saved progress from the previous attempt</p>
          ) : null}
          <ProgressBar
            value={
              review.progress && review.progress.total > 0 && (!queued || savedProgress)
                ? Math.min(100, (review.progress.completed / review.progress.total) * 100)
                : null
            }
            label={`${actor?.name ?? review.actorId} ${savedProgress ? 'saved review' : 'review'} progress`}
          />
          <p className="text-fg-muted text-xs">
            Detailed reviews can take several minutes. You can keep building; feedback will also
            arrive in Conversations.
          </p>
        </div>
      ) : null}
      {!summaryRepeatsProgress ? <FieldChatMarkdown source={summary} /> : null}
      {failed && !historical ? (
        <div className="space-y-2" role="status">
          <p className="text-fg-muted text-sm">
            {cancelled
              ? 'Your submitted code is saved. Retry this review when you’re ready to continue.'
              : 'Your submitted code is saved. Retry this review to continue.'}
          </p>
          <Button
            size="sm"
            variant="secondary"
            disabled={busy || !snapshot.capabilities.review}
            onClick={() =>
              void onAction(
                operation?.status === 'failed'
                  ? { type: 'operation.retry', operationId: operation.id }
                  : {
                      type: 'pr.request_review',
                      prId: pr.id,
                      revision: pr.revision,
                      actorIds: [review.actorId],
                      purpose: 'Retry the interrupted review of this revision.',
                    },
              )
            }
          >
            Retry {actor?.name.split(' ')[0] ?? 'colleague'} review
          </Button>
        </div>
      ) : null}
      {report ? (
        <>
          <div className="field-review-coverage" aria-label="Review coverage">
            <span>
              <strong>
                {report.coverage.filesReviewed.length} / {report.coverage.filesTotal}
              </strong>{' '}
              files inspected
            </span>
            <span>
              <strong>{report.coverage.checksObserved.length}</strong> checks observed
            </span>
            <span>
              {report.coverage.runtimeVerified
                ? 'Runtime evidence reviewed'
                : 'Runtime not verified'}
            </span>
            <span>
              {report.coverage.browserVerified
                ? 'Browser evidence reviewed'
                : 'Browser experience not verified'}
            </span>
          </div>
          {report.whatWorks.length ? (
            <section className="field-review-report-section" aria-label="What works">
              <h5>What works</h5>
              <ul className="space-y-3">
                {report.whatWorks.map((item, index) => (
                  <li key={index}>
                    <p>{item.summary}</p>
                    <Evidence
                      evidence={item.evidence}
                      snapshot={snapshot}
                      onNavigate={onNavigate}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {report.requirements.length ? (
            <section className="field-review-report-section" aria-label="Requirements checked">
              <h5>Against the agreed requirements</h5>
              <div className="overflow-x-auto">
                <table className="field-review-requirements">
                  <caption className="sr-only">
                    Requirement coverage for revision {review.revision.slice(0, 8)}
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Requirement</th>
                      <th scope="col">Result</th>
                      <th scope="col">Review</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.requirements.map((item) => (
                      <tr key={item.id}>
                        <th scope="row">{item.title}</th>
                        <td>
                          <FieldStatus value={item.status} />
                        </td>
                        <td>
                          <p>{item.explanation}</p>
                          <Evidence
                            evidence={item.evidence}
                            snapshot={snapshot}
                            onNavigate={onNavigate}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}
          {findingForTicket ? (
            <FieldTicketForm
              snapshot={snapshot}
              busy={busy}
              onAction={onAction}
              finding={findingForTicket}
              onClose={() => setTicketFinding(null)}
            />
          ) : null}
          <section className="field-review-report-section" aria-label="Detailed findings">
            <h5>
              {report.findings.length
                ? `Findings (${report.findings.length})`
                : 'No corrective findings'}
            </h5>
            {report.findings.map((finding) => {
              const record = snapshot.discovery?.findings.find((item) => item.id === finding.id);
              const resolved = record && ['resolved', 'deferred'].includes(record.status);
              return (
                <article className="field-review-finding" key={finding.id}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <h6>{finding.title}</h6>
                    <FieldStatus value={record?.status ?? finding.severity} />
                  </div>
                  <p className="field-object-meta">
                    {finding.category.replaceAll('_', ' ')} · {finding.severity} priority ·{' '}
                    {finding.confidence.replaceAll('_', ' ')}
                  </p>
                  <dl>
                    {(
                      [
                        ['Expected', finding.expected],
                        ['Observed', finding.actual],
                        ['Impact', finding.impact],
                        ['Suggested fix', finding.recommendation],
                        ['How to verify', finding.verification],
                      ] as const
                    )
                      .filter(([, value]) => value)
                      .map(([label, value]) => (
                        <div key={label}>
                          <dt>{label}</dt>
                          <dd>{value}</dd>
                        </div>
                      ))}
                  </dl>
                  <Evidence
                    evidence={finding.evidence}
                    snapshot={snapshot}
                    onNavigate={onNavigate}
                  />
                  {record?.response ? (
                    <div className="field-discovery-resolution mt-3">
                      <strong>Your response</strong>
                      <p className="whitespace-pre-wrap">{record.response}</p>
                      <p className="text-fg-muted text-xs">
                        Shared with {actor?.name ?? 'the finding owner'} in Conversations. A request
                        does not resolve or defer this finding.
                      </p>
                    </div>
                  ) : null}
                  {record?.resolution ? (
                    <div className="field-discovery-resolution mt-3">
                      <strong>Latest recorded owner decision</strong>
                      <p>{record.resolution.rationale}</p>
                      <p className="text-fg-muted text-xs">
                        {snapshot.actors.find((item) => item.id === record.resolution?.actorId)
                          ?.name ?? 'Finding owner'}{' '}
                        · <FieldTime value={record.resolution.at} />
                      </p>
                    </div>
                  ) : null}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {record && !historical && !resolved && !record.ticketId ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={busy}
                        onClick={() => setTicketFinding(record.id)}
                      >
                        Create follow-up ticket
                      </Button>
                    ) : null}
                    {record?.ticketId && onNavigate ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => onNavigate({ section: 'board', focusId: record.ticketId })}
                      >
                        View follow-up ticket
                      </Button>
                    ) : null}
                    {record && !historical && record.status !== 'resolved' ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        aria-expanded={responding === record.id}
                        onClick={() => setResponding(responding === record.id ? null : record.id)}
                      >
                        Respond to finding
                      </Button>
                    ) : null}
                    {record && (record.response || record.resolution) && onNavigate ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onNavigate({ section: 'engagement', focusId: record.id })}
                      >
                        View finding details
                      </Button>
                    ) : null}
                    {onNavigate && !historical ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          onNavigate({
                            section: 'inbox',
                            actorId: review.actorId,
                            draft: `I'd like to discuss “${finding.title}” in your review of ${pr.title} (${review.revision.slice(0, 8)}).`,
                            sourceIds: [pr.id, ...(record ? [record.id] : [])],
                          })
                        }
                      >
                        Discuss with {actor?.name.split(' ')[0]}
                      </Button>
                    ) : null}
                  </div>
                  {responding === record?.id && !historical && record.status !== 'resolved' ? (
                    <FindingResponse
                      snapshot={snapshot}
                      busy={busy}
                      onAction={onAction}
                      finding={record}
                      onClose={() => setResponding(null)}
                    />
                  ) : null}
                </article>
              );
            })}
          </section>
          {report.previousFindings.length ? (
            <section
              className="field-review-report-section"
              aria-label="Previous feedback rechecked"
            >
              <h5>Previous feedback rechecked</h5>
              <ul className="space-y-3">
                {report.previousFindings.map((item) => (
                  <li key={item.findingId}>
                    <FieldStatus value={item.status} />
                    <p className="mt-2">
                      {snapshot.discovery?.findings.find((finding) => finding.id === item.findingId)
                        ?.title ?? item.findingId}
                    </p>
                    <p>{item.explanation}</p>
                    <Evidence
                      evidence={item.evidence}
                      snapshot={snapshot}
                      onNavigate={onNavigate}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {report.limitations.length ? (
            <section className="field-review-report-section" aria-label="Review limitations">
              <h5>Not yet verified</h5>
              <ul className="list-disc space-y-1 pl-5">
                {report.limitations.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>
          ) : null}
          <details className="field-review-evidence">
            <summary>Review details</summary>
            <p>Model: {review.modelId ?? report.modelId}</p>
            <p>
              Generated <FieldTime value={report.generatedAt} />
            </p>
            <p className="break-all">
              Source revision: <code>{report.revision}</code>
            </p>
            <p>Prompt version: {report.promptVersion}</p>
            <p className="break-all">
              Context: <code>{report.contextId}</code>
            </p>
            <ul>
              {report.coverage.filesReviewed.map((path) => (
                <li key={path}>
                  <code>{path}</code>
                </li>
              ))}
            </ul>
          </details>
        </>
      ) : null}
    </div>
  );
  return (
    <section
      className="field-review-report py-4"
      aria-label={`${actor?.name ?? review.actorId} review`}
      tabIndex={-1}
      data-object-id={`review-${review.id}`}
      data-focused={focused}
    >
      <div
        className="mb-3 flex flex-wrap items-center justify-between gap-2"
        data-field-tour={report && !historical ? 'review-report' : undefined}
      >
        <h4 className="text-sm font-semibold">
          {actor?.name ?? review.actorId}{' '}
          <span className="text-fg-muted ml-2 font-normal">{review.revision.slice(0, 8)}</span>
        </h4>
        <FieldStatus
          value={
            stale
              ? 'stale'
              : superseded
                ? 'previous_evidence'
                : cancelled
                  ? 'cancelled'
                  : failed
                    ? 'failed'
                    : review.status
          }
        />
      </div>
      {historical ? (
        <details ref={history}>
          <summary className="cursor-pointer text-sm">
            {stale ? 'Previous revision review' : 'Earlier review of this revision'}
          </summary>
          {body}
        </details>
      ) : (
        body
      )}
    </section>
  );
}
